import { BigDecimal, Effect, Option, Schema, SchemaIssue, SchemaTransformation } from "effect"
import { InstrumentId, type Market, Timeframe } from "./MarketData.ts"

// Money, identifiers and the paper-trading views shared by backend and frontend
// (docs/design/0005). Every amount is a BigDecimal in memory and a plain decimal
// string on the wire ("1234.50"), never a JSON number. Times are epoch seconds UTC.

// --- Decimals -----------------------------------------------------------------------

/** Plain decimal text: optional minus, digits, optional fraction. No exponent, no "+", no ".5". */
const DECIMAL_TEXT = /^-?\d{1,30}(\.\d{1,30})?$/

/**
 * Formats a decimal as plain text, never in exponent notation (BigDecimal.format
 * switches to "1e+16" style from scale 16). Trailing zeros are dropped: "12.5".
 */
export const formatDecimal = (value: BigDecimal.BigDecimal): string => {
  const normalized = BigDecimal.normalize(value)
  const negative = normalized.value < 0n
  const digits = (negative ? -normalized.value : normalized.value).toString()
  let text: string
  if (normalized.scale <= 0) {
    text = digits === "0" ? "0" : digits + "0".repeat(-normalized.scale)
  } else {
    const padded = digits.padStart(normalized.scale + 1, "0")
    const point = padded.length - normalized.scale
    text = `${padded.slice(0, point)}.${padded.slice(point)}`
  }
  return negative ? `-${text}` : text
}

/**
 * A BigDecimal, carried as plain decimal text ("-12.50"). Decoding rejects
 * empty strings, exponents and anything that isn't a finite decimal (at most
 * 30 integer and 30 fraction digits); encoding never fails.
 */
export const Decimal = Schema.String.pipe(
  Schema.decodeTo(
    Schema.BigDecimal,
    SchemaTransformation.transformEffect({
      decode: (text: string, options) => {
        const parsed = DECIMAL_TEXT.test(text) ? BigDecimal.fromString(text) : Option.none()
        return Option.isSome(parsed)
          ? Effect.succeed(parsed.value)
          : Effect.fail(new SchemaIssue.InvalidValue({ message: "Expected a plain decimal string such as \"12.50\"" }, text, options))
      },
      encode: (value: BigDecimal.BigDecimal) => Effect.succeed(formatDecimal(value))
    })
  )
)
export type Decimal = BigDecimal.BigDecimal

const ZERO = BigDecimal.fromBigInt(0n)

/** A decimal greater than zero (quantities, prices, starting cash). */
export const PositiveDecimal = Decimal.pipe(Schema.check(Schema.isGreaterThanBigDecimal(ZERO)))

/** A decimal greater than or equal to zero (fees, filled quantity). */
export const NonNegativeDecimal = Decimal.pipe(Schema.check(Schema.isGreaterThanOrEqualToBigDecimal(ZERO)))

// --- Currencies and money ---------------------------------------------------------------

/** ISO 4217 code: three upper-case letters, e.g. "INR". */
export const CurrencyCode = Schema.String.pipe(Schema.check(Schema.isPattern(/^[A-Z]{3}$/)), Schema.brand("CurrencyCode"))
export type CurrencyCode = typeof CurrencyCode.Type

/** An amount in one currency. Money is never added across currencies without an explicit conversion. */
export const Money = Schema.Struct({
  amount: Decimal,
  currency: CurrencyCode
})
export type Money = typeof Money.Type

// --- Time ---------------------------------------------------------------------------------

/** Epoch seconds UTC. */
export const EpochSeconds = Schema.Int.pipe(Schema.check(Schema.isGreaterThanOrEqualTo(0)))

/** A calendar date "YYYY-MM-DD" (an FX fixing date, a venue-local trading day). */
export const IsoDate = Schema.String.pipe(
  Schema.check(Schema.isPattern(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/)),
  Schema.brand("IsoDate")
)
export type IsoDate = typeof IsoDate.Type

// --- Identifiers -------------------------------------------------------------------------

const ID = /^[A-Za-z0-9_-]{1,64}$/

/** A paper (later live) trading account, e.g. "acc_3f9k2". */
export const AccountId = Schema.String.pipe(Schema.check(Schema.isPattern(ID)), Schema.brand("AccountId"))
export type AccountId = typeof AccountId.Type

/** An order within one account: "ord_<n>", a per-account counter folded from the journal. */
export const OrderId = Schema.String.pipe(Schema.check(Schema.isPattern(ID)), Schema.brand("OrderId"))
export type OrderId = typeof OrderId.Type

/** A strategy attached to an account (P3). */
export const RunnerId = Schema.String.pipe(Schema.check(Schema.isPattern(ID)), Schema.brand("RunnerId"))
export type RunnerId = typeof RunnerId.Type

/** A user-written strategy (P4); its versions are numbered from 1. */
export const StrategyId = Schema.String.pipe(Schema.check(Schema.isPattern(ID)), Schema.brand("StrategyId"))
export type StrategyId = typeof StrategyId.Type

/**
 * The caller's idempotency key for one order. The ticket mints one when it opens
 * (and again after any 4xx); runners use "r:<runnerId>:<barTime>:<k>". Repeating
 * a cid with the same payload returns the existing order instead of a new one.
 */
export const ClientOrderId = Schema.String.pipe(
  Schema.check(Schema.isPattern(/^[A-Za-z0-9:_-]{1,64}$/)),
  Schema.brand("ClientOrderId")
)
export type ClientOrderId = typeof ClientOrderId.Type

/** One execution. Paper fills use a deterministic hash of (orderId, barTime, leg). */
export const FillId = Schema.String.pipe(Schema.check(Schema.isPattern(/^[A-Za-z0-9:_-]{1,128}$/)), Schema.brand("FillId"))
export type FillId = typeof FillId.Type

/**
 * Which strategy a runner or the tester runs: a catalogue id ("builtin:ma-cross",
 * "script:…", "port:…"), a user strategy following its latest version
 * ("user:<strategyId>") or pinned to one ("user:<strategyId>@<version>").
 */
export const StrategyRef = Schema.String.pipe(
  Schema.check(Schema.isPattern(/^(?:(?:builtin|script|port):[A-Za-z0-9._-]{1,100}|user:[A-Za-z0-9_-]{1,64}(?:@[1-9]\d{0,8})?)$/)),
  Schema.brand("StrategyRef")
)
export type StrategyRef = typeof StrategyRef.Type

// --- Order fields -------------------------------------------------------------------------

export const Side = Schema.Literals(["Buy", "Sell"])
export type Side = typeof Side.Type

/** Fills at the next bar's open (plus slippage). */
export const MarketOrderType = Schema.TaggedStruct("Market", {})
/** Buys at or below / sells at or above `limitPrice`. */
export const LimitOrderType = Schema.TaggedStruct("Limit", { limitPrice: PositiveDecimal })
/** Becomes a market order once the price trades through `stopPrice`. */
export const StopOrderType = Schema.TaggedStruct("Stop", { stopPrice: PositiveDecimal })
/** Becomes a limit order at `limitPrice` once the price trades through `stopPrice`. */
export const StopLimitOrderType = Schema.TaggedStruct("StopLimit", { stopPrice: PositiveDecimal, limitPrice: PositiveDecimal })

/** Prices are in the instrument's currency and must sit on its tick grid (checked by risk, not here). */
export const OrderType = Schema.Union([MarketOrderType, LimitOrderType, StopOrderType, StopLimitOrderType])
export type OrderType = typeof OrderType.Type

/** Day orders expire on the first bar of a later venue-local date; GTC orders stay until filled or cancelled. */
export const TimeInForce = Schema.Literals(["Day", "GTC"])
export type TimeInForce = typeof TimeInForce.Type

/**
 * Cash: delivery, no leverage. Intraday: squared off at the account's
 * `squareOffTime` (India MIS). Carryforward: held overnight (India NRML).
 * Positions net per (account, instrument, product).
 */
export const Product = Schema.Literals(["Cash", "Intraday", "Carryforward"])
export type Product = typeof Product.Type

/**
 * Products each market's paper venue accepts. India: CNC, MIS and NRML. Elsewhere Cash and
 * Carryforward (docs/design/0013): CFDs and foreign stocks can be sold short and held overnight;
 * Intraday's square-off follows the Indian session, so it stays India-only.
 */
export const PRODUCTS_BY_MARKET: Readonly<Record<Market, ReadonlyArray<Product>>> = {
  IN: ["Cash", "Intraday", "Carryforward"],
  US: ["Cash", "Carryforward"],
  JP: ["Cash", "Carryforward"],
  DE: ["Cash", "Carryforward"],
  CFD: ["Cash", "Carryforward"]
}

/** Selling beyond the held quantity: Intraday and Carryforward; Cash sells only what it holds. */
export const productAllowsShort = (product: Product): boolean => product === "Intraday" || product === "Carryforward"

/**
 * A stop-loss and a take-profit on a position (docs/design/0013): each closes the whole position,
 * and when one fills the other is cancelled.
 */
export const Protection = Schema.Struct({
  stopLoss: Schema.optionalKey(PositiveDecimal),
  takeProfit: Schema.optionalKey(PositiveDecimal)
})
export type Protection = typeof Protection.Type

/** A new order. The frontend validates its ticket with this same schema. */
export const PlaceOrder = Schema.Struct({
  clientOrderId: ClientOrderId,
  instrumentId: InstrumentId,
  side: Side,
  /** Units (shares); a positive multiple of the instrument's lot size. */
  qty: PositiveDecimal,
  type: OrderType,
  product: Product,
  tif: TimeInForce,
  /** Put on the position when this order fills (when it opens or adds to the position). */
  protection: Schema.optionalKey(Protection)
})
export type PlaceOrder = typeof PlaceOrder.Type

/**
 * Changes to a working order (docs/design/0013): new prices (only the ones its type has), and/or
 * the stop-loss and take-profit it puts on the position when it fills (both given; null: none).
 */
export const AmendOrder = Schema.Struct({
  limitPrice: Schema.optionalKey(PositiveDecimal),
  stopPrice: Schema.optionalKey(PositiveDecimal),
  protection: Schema.optionalKey(Schema.Struct({
    stopLoss: Schema.NullOr(PositiveDecimal),
    takeProfit: Schema.NullOr(PositiveDecimal)
  }))
})
export type AmendOrder = typeof AmendOrder.Type

/** At most this many take-profits on a position (docs/design/0017). */
export const MAX_TAKE_PROFITS = 4

/**
 * A position's size split equally between its take-profits on the quantity `step` grid, the
 * extra steps to the first (nearest) ones: 10 in 3 → 4, 3, 3 (docs/design/0017). Null when a part
 * would be empty (fewer steps than parts).
 */
export const splitQty = (size: BigDecimal.BigDecimal, n: number, step: BigDecimal.BigDecimal): Array<BigDecimal.BigDecimal> | null => {
  if (n < 1) return []
  const whole = Math.floor(BigDecimal.toNumberUnsafe(BigDecimal.divideUnsafe(size, step)) + 1e-9)
  const base = Math.floor(whole / n)
  if (base < 1) return null
  const extra = whole - base * n
  const parts = Array.from({ length: n }, (_, i) => BigDecimal.multiply(step, BigDecimal.fromBigInt(BigInt(base + (i < extra ? 1 : 0)))))
  // A size off the grid (it shouldn't be): the rest goes to the first part, so the parts add up.
  const rest = BigDecimal.subtract(size, BigDecimal.multiply(step, BigDecimal.fromBigInt(BigInt(whole))))
  if (!BigDecimal.isZero(rest)) parts[0] = BigDecimal.sum(parts[0]!, rest)
  return parts
}

/**
 * A position's stop-loss and take-profit, set, moved (a new price) or removed (null).
 * `takeProfits` (docs/design/0017), when given, replaces `takeProfit`: 0–4 targets, the position
 * split equally between them.
 */
export const SetProtection = Schema.Struct({
  product: Product,
  stopLoss: Schema.NullOr(PositiveDecimal),
  takeProfit: Schema.NullOr(PositiveDecimal),
  takeProfits: Schema.optionalKey(Schema.Array(PositiveDecimal).pipe(Schema.check(Schema.isMaxLength(MAX_TAKE_PROFITS))))
})
export type SetProtection = typeof SetProtection.Type

/** Why risk refused an order (`OrderDenied`). A denied order never reached the venue. */
export const DenyReason = Schema.Literals([
  "UnknownInstrument",
  "NotTradeable",
  "CurrencyMismatch",
  "InvalidQty",
  "InvalidLotSize",
  "OffTickGrid",
  "TypeFields",
  "ProductNotAllowed",
  "ShortNotAllowed",
  "NoReferencePrice",
  "InsufficientBuyingPower",
  "PositionOwnedByRunner",
  "AccountHalted"
])
export type DenyReason = typeof DenyReason.Type

// --- Account clock ---------------------------------------------------------------------------

/** Advances on stored bars as they land (later on live ticks). */
export const RealtimeClockSpec = Schema.TaggedStruct("Realtime", { timeframe: Timeframe })
/** A virtual clock over stored bars from `from`, driven by play / pause / step. */
export const ReplayClockSpec = Schema.TaggedStruct("Replay", { timeframe: Timeframe, from: EpochSeconds })

/** Chosen when an account is created; one timeframe per account. */
export const AccountClock = Schema.Union([RealtimeClockSpec, ReplayClockSpec])
export type AccountClock = typeof AccountClock.Type

/** Replay speed in bars per second (0.5: one bar every 2 s); "max" runs as fast as the engine can step. */
export const REPLAY_SPEEDS = [0.5, 1, 5, 20, "max"] as const
export const ReplaySpeed = Schema.Literals(REPLAY_SPEEDS)
export type ReplaySpeed = typeof ReplaySpeed.Type

/**
 * Instruments a replay follows besides what the account trades: the open charts' (docs/design/0011).
 * The clock steps through their bars too, so every chart moves; following trades nothing.
 */
export const FollowList = Schema.Array(InstrumentId).pipe(Schema.check(Schema.isMaxLength(16)))

/** Commands for a Replay account's clock (`POST /api/accounts/:id/clock`). */
export const ClockCommand = Schema.Union([
  Schema.TaggedStruct("Play", { speed: ReplaySpeed, follow: Schema.optionalKey(FollowList) }),
  Schema.TaggedStruct("Pause", {}),
  Schema.TaggedStruct("Step", {
    bars: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 1, maximum: 1000 }))),
    follow: Schema.optionalKey(FollowList)
  }),
  /**
   * Moves the replay forward, as fast as the engine steps, through every bar that
   * closes by `to` (orders fill and runners decide on each, as if played), then
   * pauses (docs/design/0007: reusing an account for a replay from a later bar).
   * A `to` at or before the replay's time changes nothing.
   */
  Schema.TaggedStruct("Advance", { to: EpochSeconds })
])
export type ClockCommand = typeof ClockCommand.Type

/**
 * Where an account's clock stands. `now` is the account's market time: every
 * bar that closed at or before it has been processed. A new Realtime account
 * starts at its creation time; a Replay account at `from`.
 */
export const ClockState = Schema.Union([
  Schema.TaggedStruct("Realtime", { timeframe: Timeframe, now: EpochSeconds }),
  Schema.TaggedStruct("Replay", {
    timeframe: Timeframe,
    from: EpochSeconds,
    now: EpochSeconds,
    status: Schema.Literals(["Paused", "Playing", "Finished"]),
    /** The last speed chosen; kept while paused. */
    speed: ReplaySpeed
  })
])
export type ClockState = typeof ClockState.Type

// --- Account configuration ----------------------------------------------------------------------

/**
 * How limit orders fill. engine-parity: when the bar touches the limit (matches
 * the Strategy Tester). conservative: only when the price trades through it.
 */
export const FillModelId = Schema.Literals(["engine-parity", "conservative"])
export type FillModelId = typeof FillModelId.Type

/** Fees booked on each fill, in the account currency. */
export const ChargeModel = Schema.Union([
  Schema.TaggedStruct("Zero", {}),
  /** `bps` basis points of the fill notional, at least `min` per fill. */
  Schema.TaggedStruct("Percent", { bps: NonNegativeDecimal, min: NonNegativeDecimal }),
  /** A flat amount per fill. */
  Schema.TaggedStruct("PerOrder", { amount: NonNegativeDecimal }),
  /** Fyers NSE equity: brokerage, STT, exchange, SEBI, stamp duty and GST, by product (P6). */
  Schema.TaggedStruct("FyersNseEquity", {})
])
export type ChargeModel = typeof ChargeModel.Type

/** Margin as a percent of position value: 100 is a plain cash account. */
export const Margins = Schema.Struct({
  long: PositiveDecimal,
  short: PositiveDecimal
})
export type Margins = typeof Margins.Type

/** Venue-local wall-clock time "HH:MM". */
export const LocalTime = Schema.String.pipe(Schema.check(Schema.isPattern(/^([01]\d|2[0-3]):[0-5]\d$/)))

export const AccountConfig = Schema.Struct({
  fillModel: FillModelId,
  chargeModel: ChargeModel,
  /** Ticks against the order on market and stop fills. */
  slippageTicks: Schema.Int.pipe(Schema.check(Schema.isBetween({ minimum: 0, maximum: 100 }))),
  /** Short selling (Sell beyond the held quantity). Off by default. */
  /** Unused since shorting became a product rule (Intraday, Carryforward); kept so old journals decode. */
  allowShort: Schema.Boolean,
  margins: Margins,
  /** When Intraday positions are squared off, in the venue's local time (NSE: 15:15). */
  squareOffTime: LocalTime
})
export type AccountConfig = typeof AccountConfig.Type

/** What `POST /api/accounts` may override; anything left out takes DEFAULT_ACCOUNT_CONFIG. */
export const AccountConfigPatch = Schema.Struct({
  fillModel: Schema.optionalKey(FillModelId),
  chargeModel: Schema.optionalKey(ChargeModel),
  slippageTicks: Schema.optionalKey(AccountConfig.fields.slippageTicks),
  allowShort: Schema.optionalKey(Schema.Boolean),
  margins: Schema.optionalKey(Margins),
  squareOffTime: Schema.optionalKey(LocalTime)
})
export type AccountConfigPatch = typeof AccountConfigPatch.Type

const HUNDRED = BigDecimal.fromBigInt(100n)

/** A plain cash account: touch fills, no fees, no slippage, no shorting, 100% margin. */
export const DEFAULT_ACCOUNT_CONFIG: AccountConfig = {
  fillModel: "engine-parity",
  chargeModel: { _tag: "Zero" },
  slippageTicks: 0,
  allowShort: false,
  margins: { long: HUNDRED, short: HUNDRED },
  squareOffTime: "15:15"
}

/** Applies a creation patch over the defaults. */
export const resolveAccountConfig = (patch: AccountConfigPatch | undefined): AccountConfig => ({
  ...DEFAULT_ACCOUNT_CONFIG,
  ...patch
})

// --- Accounts -----------------------------------------------------------------------------------

export const AccountName = Schema.String.pipe(
  Schema.check(Schema.isTrimmed(), Schema.isMinLength(1), Schema.isMaxLength(64))
)

/** `PATCH /api/accounts/:id` (docs/design/0007). */
export const RenameAccount = Schema.Struct({ name: AccountName })
export type RenameAccount = typeof RenameAccount.Type

/** At most 10^15 in the account currency. */
const MAX_STARTING_CASH = BigDecimal.fromBigInt(10n ** 15n)

export const CreateAccount = Schema.Struct({
  name: AccountName,
  /** The account's only currency (D4); the frontend defaults it to the active market's currency. */
  currency: CurrencyCode,
  startingCash: PositiveDecimal.pipe(Schema.check(Schema.isLessThanOrEqualToBigDecimal(MAX_STARTING_CASH))),
  clock: AccountClock,
  config: Schema.optionalKey(AccountConfigPatch)
})
export type CreateAccount = typeof CreateAccount.Type

/** Active ⇄ Halted (needs an operator to reconcile; new orders are denied). Archived after a reset. */
export const AccountStatus = Schema.Literals(["Active", "Halted", "Archived"])
export type AccountStatus = typeof AccountStatus.Type

/** An account's balances and state. All amounts are in `currency`. */
export const AccountSummary = Schema.Struct({
  id: AccountId,
  name: Schema.String,
  /** Later also "live:fyers:<account>". */
  mode: Schema.Literal("paper"),
  currency: CurrencyCode,
  status: AccountStatus,
  /** Why the account is Halted; null otherwise. */
  haltReason: Schema.NullOr(Schema.String),
  startingCash: PositiveDecimal,
  /** startingCash + sell notionals − buy notionals − fees. */
  cash: Decimal,
  /** cash + Σ qty × mark × pointValue. */
  equity: Decimal,
  /** What new orders may still commit: equity minus margin in use and open buy orders' reserve. */
  buyingPower: Decimal,
  realizedPnl: Decimal,
  unrealizedPnl: Decimal,
  /** Fees booked so far. */
  fees: NonNegativeDecimal,
  clock: ClockState,
  /** Marks come from a bar that closed at this time and nothing newer has arrived; null when fresh. */
  staleSince: Schema.NullOr(EpochSeconds),
  config: AccountConfig,
  /** The account this one replaced through a reset. */
  resetOf: Schema.NullOr(AccountId),
  /** Wall-clock times. */
  createdAt: EpochSeconds,
  archivedAt: Schema.NullOr(EpochSeconds),
  /** Account-clock time the session was ended (docs/design/0008); null unless ended. A reset archives without ending. */
  endedAt: Schema.NullOr(EpochSeconds)
})
export type AccountSummary = typeof AccountSummary.Type

// --- Orders, fills, positions -------------------------------------------------------------------

export const ORDER_STATUSES = [
  "PendingSubmit",
  "Working",
  "Triggered",
  "PartiallyFilled",
  "PendingCancel",
  "Filled",
  "Cancelled",
  "Rejected",
  "Expired",
  "Denied"
] as const
export const OrderStatus = Schema.Literals(ORDER_STATUSES)
export type OrderStatus = typeof OrderStatus.Type

const TERMINAL_STATUSES: ReadonlySet<OrderStatus> = new Set(["Filled", "Cancelled", "Rejected", "Expired", "Denied"])

/** True while an order can still fill or be cancelled. */
export const isOpenOrderStatus = (status: OrderStatus): boolean => !TERMINAL_STATUSES.has(status)

/**
 * Entry: placed by the user, a strategy or a square-off. StopLoss / TakeProfit: a position's
 * protection (docs/design/0013), a Stop / Limit that closes the whole position.
 */
export const OrderRole = Schema.Literals(["Entry", "StopLoss", "TakeProfit"])
export type OrderRole = typeof OrderRole.Type

export const Order = Schema.Struct({
  id: OrderId,
  clientOrderId: ClientOrderId,
  /** Journal seq of the event that created the order; the pagination cursor. */
  seq: Schema.Int,
  instrumentId: InstrumentId,
  side: Side,
  qty: PositiveDecimal,
  type: OrderType,
  product: Product,
  tif: TimeInForce,
  /** The runner that placed it; null for a manual order. */
  runnerId: Schema.NullOr(RunnerId),
  role: OrderRole,
  /** A working entry's stop-loss and take-profit, put on the position when it fills; null when none. */
  protection: Schema.NullOr(Protection),
  status: OrderStatus,
  /** The venue outcome of a submit or cancel is unknown until reconciled (live only). */
  needsReconcile: Schema.Boolean,
  filledQty: NonNegativeDecimal,
  avgFillPrice: Schema.NullOr(PositiveDecimal),
  /** Why it was denied, rejected, cancelled or expired; null otherwise. */
  reason: Schema.NullOr(Schema.String),
  /** Account-clock times (virtual in replay). */
  createdAt: EpochSeconds,
  updatedAt: EpochSeconds
})
export type Order = typeof Order.Type

export const ChargeLine = Schema.Struct({
  /** e.g. "brokerage", "stt", "exchange", "sebi", "stamp", "gst". */
  name: Schema.String,
  amount: NonNegativeDecimal
})

/** Fees on one fill, in the account currency, rounded to minor units when booked. */
export const ChargeBreakdown = Schema.Struct({
  total: NonNegativeDecimal,
  lines: Schema.Array(ChargeLine)
})
export type ChargeBreakdown = typeof ChargeBreakdown.Type

/**
 * The rate money in an instrument's currency was converted at into the account's
 * (docs/design/0018): units of the account's currency per 1 unit of the instrument's,
 * from the last fixing dated before the trading day (ECB, through EUR).
 */
export const FxConversion = Schema.Struct({
  rate: PositiveDecimal,
  fixingDate: IsoDate,
  /** e.g. "ecb". */
  source: Schema.String
})
export type FxConversion = typeof FxConversion.Type

export const Fill = Schema.Struct({
  id: FillId,
  /** Journal seq of the fill event; the pagination cursor. */
  seq: Schema.Int,
  orderId: OrderId,
  clientOrderId: ClientOrderId,
  runnerId: Schema.NullOr(RunnerId),
  instrumentId: InstrumentId,
  side: Side,
  product: Product,
  qty: PositiveDecimal,
  price: PositiveDecimal,
  /** price × qty × pointValue, exact, in the instrument's currency. */
  notional: PositiveDecimal,
  /** In the account's currency. */
  fee: ChargeBreakdown,
  /**
   * P&L this fill realized (zero when it only opens or adds), in the account's currency. In the
   * instrument's own currency it is a reporting figure; in another, it is what cash moves by
   * (docs/design/0018).
   */
  realizedPnl: Decimal,
  /** The rate its money was converted at; null when the instrument trades in the account's currency. */
  fx: Schema.NullOr(FxConversion),
  /** Account-clock time of the fill (the open time of the bar it filled on). */
  time: EpochSeconds
})
export type Fill = typeof Fill.Type

/**
 * Net position per (instrument, product). `qty` is signed: positive long,
 * negative short, zero once flat (flat positions stay listed with their realized P&L).
 * Marks and unrealized P&L change every bar and also arrive in the topic status.
 */
/** One working protective order of a position: which order, at what price. */
export const ProtectiveOrder = Schema.Struct({ orderId: OrderId, price: PositiveDecimal })
export type ProtectiveOrder = typeof ProtectiveOrder.Type

/** One take-profit of a position (docs/design/0017): its order, price and the part it closes. */
export const TakeProfitTarget = Schema.Struct({ orderId: OrderId, price: PositiveDecimal, qty: PositiveDecimal })
export type TakeProfitTarget = typeof TakeProfitTarget.Type

export const Position = Schema.Struct({
  instrumentId: InstrumentId,
  product: Product,
  /** The instrument's currency: its prices (avgPrice, mark, SL / TP) are in it. */
  currency: CurrencyCode,
  qty: Decimal,
  /** Weighted average entry price; null when flat. */
  avgPrice: Schema.NullOr(PositiveDecimal),
  /** Last bar close; null before the first bar after the position opened. */
  mark: Schema.NullOr(PositiveDecimal),
  markTime: Schema.NullOr(EpochSeconds),
  /** In the account's currency, converted at `fx` (docs/design/0018). */
  unrealizedPnl: Decimal,
  /** In the account's currency: each fill's at its own rate. */
  realizedPnl: Decimal,
  /** Today's rate its open P&L is converted at; null in the account's currency. */
  fx: Schema.NullOr(FxConversion),
  /** The runner that owns it (manual orders on it are denied while the runner runs); null when manual. */
  runnerId: Schema.NullOr(RunnerId),
  /** Its working protective orders (docs/design/0013); null when there is none. */
  stopLoss: Schema.NullOr(ProtectiveOrder),
  /** TP1, the nearest take-profit (as before docs/design/0017). */
  takeProfit: Schema.NullOr(ProtectiveOrder),
  /** Every take-profit, nearest the entry first (docs/design/0017). */
  takeProfits: Schema.Array(TakeProfitTarget)
})
export type Position = typeof Position.Type

/** The per-bar part of a position, sent latest-wins in the account topic's status. */
export const PositionMark = Schema.Struct({
  instrumentId: InstrumentId,
  product: Product,
  mark: Schema.NullOr(PositiveDecimal),
  markTime: Schema.NullOr(EpochSeconds),
  unrealizedPnl: Decimal,
  fx: Schema.NullOr(FxConversion)
})
export type PositionMark = typeof PositionMark.Type

// --- Runners (P3) --------------------------------------------------------------------------------

/** How a runner turns the strategy's evaluation into orders. target-position: v1. working-orders: P6. */
export const PlannerId = Schema.Literals(["target-position", "working-orders"])
export type PlannerId = typeof PlannerId.Type

/** waitForFlat: hold the target at 0 until the strategy is first flat after attaching. adopt: follow it at once. */
export const StartPolicy = Schema.Literals(["waitForFlat", "adopt"])
export type StartPolicy = typeof StartPolicy.Type

/**
 * Which entries the strategy may make (Pine's strategy.risk.allow_entry_in). all: as
 * backtested. long: a short entry only closes the long position, for products that
 * can't sell short (Cash); the strategy's own run follows the same rule.
 */
export const RunnerDirection = Schema.Literals(["all", "long"])
export type RunnerDirection = typeof RunnerDirection.Type

export const RunnerHaltReason = Schema.Literals(["Diverged", "NonDeterministic", "RuntimeError", "Denied", "AccountHalted"])
export type RunnerHaltReason = typeof RunnerHaltReason.Type

/** Starting → Arming → Running ⇄ Paused; Halted on failure; Stopping{flatten} → Stopped (terminal). */
export const RunnerStatus = Schema.Union([
  Schema.TaggedStruct("Starting", {}),
  Schema.TaggedStruct("Arming", {}),
  Schema.TaggedStruct("Running", {}),
  Schema.TaggedStruct("Paused", {}),
  Schema.TaggedStruct("Halted", { reason: RunnerHaltReason, message: Schema.String }),
  Schema.TaggedStruct("Stopping", { flatten: Schema.Boolean }),
  Schema.TaggedStruct("Stopped", {})
])
export type RunnerStatus = typeof RunnerStatus.Type

/** Strategy inputs and engine properties that differ from the strategy's own (as in the tester's pane state). */
export const StrategyOverrides = Schema.JsonObject

export const Runner = Schema.Struct({
  id: RunnerId,
  accountId: AccountId,
  /** Pinned: a user strategy is always "user:<id>@<version>" here. */
  strategy: StrategyRef,
  strategyName: Schema.String,
  /** Hash of the worker bundle that evaluates it. */
  engineBuild: Schema.String,
  instrumentId: InstrumentId,
  /** The bars it runs on: the account's, or the chart's on a 1m replay account (docs/design/0011). */
  timeframe: Timeframe,
  product: Product,
  inputs: StrategyOverrides,
  properties: StrategyOverrides,
  planner: PlannerId,
  startPolicy: StartPolicy,
  direction: RunnerDirection,
  /** The fixed first bar of every evaluation window (startTime − warmup). */
  anchorTime: EpochSeconds,
  startTime: EpochSeconds,
  status: RunnerStatus,
  /** The last closed bar it decided on; null before the first. */
  lastBarTime: Schema.NullOr(EpochSeconds),
  /** The runner-owned position quantity, signed. */
  position: Decimal,
  createdAt: EpochSeconds,
  updatedAt: EpochSeconds
})
export type Runner = typeof Runner.Type

export const AttachRunner = Schema.Struct({
  /** "user:<id>" is pinned to that strategy's latest version on attach. */
  strategy: StrategyRef,
  instrumentId: InstrumentId,
  product: Product,
  inputs: Schema.optionalKey(StrategyOverrides),
  properties: Schema.optionalKey(StrategyOverrides),
  /** Default waitForFlat. */
  startPolicy: Schema.optionalKey(StartPolicy),
  /** Default target-position. */
  planner: Schema.optionalKey(PlannerId),
  /** Default all; a strategy that goes short needs a product that can sell short, or long. */
  direction: Schema.optionalKey(RunnerDirection),
  /**
   * The bars the strategy runs on; default the account's. A 1m replay account runs a 15m strategy on
   * 15m bars, deciding as each closes, and fills its orders on the next 1m bars (docs/design/0011).
   */
  timeframe: Schema.optionalKey(Timeframe)
})
export type AttachRunner = typeof AttachRunner.Type

export const StopRunner = Schema.Struct({
  /** Also close the runner's position with a market order. */
  flatten: Schema.Boolean
})
export type StopRunner = typeof StopRunner.Type

/** What a runner did on one closed bar (the `runner_decisions` audit row). */
export const RunnerDecisionOutcome = Schema.Union([
  Schema.TaggedStruct("Hold", {}),
  /** One of its orders was still in flight. */
  Schema.TaggedStruct("Wait", {}),
  Schema.TaggedStruct("Placed", { clientOrderId: ClientOrderId }),
  Schema.TaggedStruct("Denied", { reason: DenyReason }),
  Schema.TaggedStruct("Reanchored", {})
])
export type RunnerDecisionOutcome = typeof RunnerDecisionOutcome.Type

export const RunnerDecision = Schema.Struct({
  barTime: EpochSeconds,
  /** The engine's target position on this bar, signed. */
  target: Decimal,
  fillCount: Schema.Int,
  outcome: RunnerDecisionOutcome,
  evalMs: Schema.Number
})
export type RunnerDecision = typeof RunnerDecision.Type

/** A fill as the strategy engine saw it (analysis numbers, not money). */
export const EngineFill = Schema.Struct({
  /** Open time of the fill bar. */
  time: EpochSeconds,
  side: Side,
  qty: Schema.Number,
  price: Schema.Number
})
export type EngineFill = typeof EngineFill.Type

/** The forward report: the engine's fills next to the paper fills, plus recent decisions. */
export const RunnerReport = Schema.Struct({
  runner: Runner,
  engineFills: Schema.Array(EngineFill),
  paperFills: Schema.Array(Fill),
  /** Newest first. */
  decisions: Schema.Array(RunnerDecision)
})
export type RunnerReport = typeof RunnerReport.Type

// --- Snapshots and pages --------------------------------------------------------------------------

/**
 * An account's full state: `GET /api/accounts/:id`, and the account topic's
 * snapshot. `journalSeq` is the last journal entry it reflects.
 */
export const AccountSnapshot = Schema.TaggedStruct("AccountSnapshot", {
  summary: AccountSummary,
  /** Every (instrument, product) the account has traded; flat ones have qty 0. */
  positions: Schema.Array(Position),
  /** Orders whose status is open (isOpenOrderStatus). */
  openOrders: Schema.Array(Order),
  runners: Schema.Array(Runner),
  journalSeq: Schema.Int
})
export type AccountSnapshot = typeof AccountSnapshot.Type

/** Newest first. Pass `nextBefore` as `before` for the next page; null when there is none. */
export const OrdersPage = Schema.Struct({
  orders: Schema.Array(Order),
  nextBefore: Schema.NullOr(Schema.Int)
})
export type OrdersPage = typeof OrdersPage.Type

/** Newest first. Pass `nextBefore` as `before` for the next page; null when there is none. */
export const FillsPage = Schema.Struct({
  fills: Schema.Array(Fill),
  nextBefore: Schema.NullOr(Schema.Int)
})
export type FillsPage = typeof FillsPage.Type

// --- Session summary (docs/design/0008) ------------------------------------------------------------

export const TradeDirection = Schema.Literals(["Long", "Short"])
export type TradeDirection = typeof TradeDirection.Type

/**
 * One closed round trip on an (instrument, product): from the fill that took the position
 * off zero to the one that brought it back (a fill that reverses it is split at zero).
 */
export const RoundTrip = Schema.Struct({
  instrumentId: InstrumentId,
  product: Product,
  direction: TradeDirection,
  /** The largest quantity held during the trip. */
  qty: PositiveDecimal,
  /** Account-clock times. */
  entryTime: EpochSeconds,
  exitTime: EpochSeconds,
  /** Quantity-weighted averages of the entry and exit fills. */
  entryPrice: PositiveDecimal,
  exitPrice: PositiveDecimal,
  realizedPnl: Decimal,
  /** Fees on the trip's fills (a split fill's pro rata by quantity). */
  charges: NonNegativeDecimal,
  /** realizedPnl − charges. */
  netPnl: Decimal,
  fills: Schema.Int
})
export type RoundTrip = typeof RoundTrip.Type

/** Totals over the closed trips. Ratios are numbers (analysis, not money); null when undefined. */
export const SessionStats = Schema.Struct({
  trades: Schema.Int,
  wins: Schema.Int,
  losses: Schema.Int,
  /** wins / trades, 0 to 1; null without trades. */
  winRate: Schema.NullOr(Schema.Number),
  grossProfit: NonNegativeDecimal,
  /** As a positive amount. */
  grossLoss: NonNegativeDecimal,
  /** grossProfit / grossLoss; null without a losing trade. */
  profitFactor: Schema.NullOr(Schema.Number),
  avgWin: Schema.NullOr(Decimal),
  avgLoss: Schema.NullOr(Decimal),
  largestWin: Schema.NullOr(Decimal),
  largestLoss: Schema.NullOr(Decimal),
  /** Largest peak-to-trough fall of closed-trade equity (starting cash + Σ trip net P&L), as a positive amount. */
  maxDrawdown: NonNegativeDecimal,
  /** maxDrawdown / that peak, 0 to 1. */
  maxDrawdownPct: Schema.Number
})
export type SessionStats = typeof SessionStats.Type

/** How a paper session went: live while it runs, frozen at its final marks once ended. */
export const SessionSummary = Schema.Struct({
  accountId: AccountId,
  name: Schema.String,
  currency: CurrencyCode,
  clock: Schema.Literals(["Realtime", "Replay"]),
  /** Account-clock span: the session's start, and its end (or its market time while it runs). */
  from: EpochSeconds,
  to: EpochSeconds,
  endedAt: Schema.NullOr(EpochSeconds),
  startingCash: PositiveDecimal,
  endingEquity: Decimal,
  netPnl: Decimal,
  /** netPnl / startingCash × 100. */
  netPnlPct: Schema.Number,
  realizedPnl: Decimal,
  unrealizedPnl: Decimal,
  charges: NonNegativeDecimal,
  /** Closed trips, oldest exit first. */
  trades: Schema.Array(RoundTrip),
  /** Positions still open (at the end, valued at the final marks). */
  openPositions: Schema.Array(Position),
  stats: SessionStats
})
export type SessionSummary = typeof SessionSummary.Type
